import { z } from "zod";
import { BlockSchema } from "./blocks";
import { MediaRefSchema, SlugSchema } from "./common";

export const PageKindSchema = z.enum(["page", "post", "changelog", "product", "legal"]);
export type PageKind = z.infer<typeof PageKindSchema>;

export const TagSchema = z
  .string()
  .min(1)
  .max(40)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "lowercase letters, digits and single hyphens");

/** Blog-post specifics (author, cover, tags); stored in the revision meta under `post`. */
export const PostMetaSchema = z.object({
  author_name: z.string().max(80).optional(),
  author_role: z.string().max(80).optional(),
  author_avatar: MediaRefSchema.optional(),
  cover: MediaRefSchema.optional(),
  tags: z.array(TagSchema).max(8).default([]),
  /** Shown as the large lead card at the top of the blog index. */
  featured: z.boolean().default(false),
});
export type PostMeta = z.infer<typeof PostMetaSchema>;

export const PageMetaSchema = z.object({
  post: PostMetaSchema.optional(),
  seo_title: z.string().max(70).optional(),
  seo_description: z.string().max(200).optional(),
  og_image_id: z.string().max(64).optional(),
  canonical: z.url().optional(),
  noindex: z.boolean().default(false),
});
export type PageMeta = z.infer<typeof PageMetaSchema>;

export const PageSchema = z.object({
  slug: SlugSchema,
  kind: PageKindSchema,
  title: z.string().min(1).max(160),
  /** Short excerpt for posts/changelog lists. */
  excerpt: z.string().max(400).optional(),
  blocks: z.array(BlockSchema).max(60),
  meta: PageMetaSchema.default({ noindex: false }),
  /** ISO timestamps; set by the API. */
  published_at: z.iso.datetime().optional(),
  updated_at: z.iso.datetime().optional(),
});
export type Page = z.infer<typeof PageSchema>;
export type PageInput = z.input<typeof PageSchema>;
